/**
 * Was the interval before this frame **observed**? One definition, for everything that holds a claim
 * about watched time (D-090, S5-1/S5-3/S5-5/S5-9).
 *
 * The rule this whole family turns on is short: *a claim about a stretch of time is only as good as
 * the frames that stretch was actually watched in.* A posture held, a jump in the air, a repetition
 * part-way through, a stretch of stillness, eight hundred milliseconds of standing still for a
 * calibration — all of them are statements about watched time, and a hole in the frames is the part
 * nobody watched. Round 4 learned this for postures; round 5 found the same subtraction in three
 * more places and in the calibration hold.
 *
 * So the *question* lives here, exactly once, rather than as a fourth copy of `at - previous > 400`
 * written at a fourth call site. Two constants for one physical fact is how the halves of a
 * discontinuity come to disagree, and the same is true of two predicates.
 *
 * **What it deliberately does not decide** is what to *do* about a hole. That is always local, and
 * always different: the camera adapter retracts and re-baselines its detectors, the calibration flow
 * throws away a part-finished hold, and the tracking gate's loss clock — the one clock in the system
 * measured *from* the last time a body could be seen — is never touched by any of them.
 *
 * A backwards clock counts as a hole too. A timestamp that has gone the wrong way is not a run of
 * frames anybody has been following: something restarted, was corrected, or arrived out of order,
 * and nothing before it is continuous with what comes next.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { GameplayTunables } from '../../shared/types/tunables';

export interface ObservationContinuity {
  /**
   * Records that a frame stamped `at` has arrived, and answers whether the interval before it was a
   * hole. `false` for the very first frame: there is no interval before it to have missed.
   */
  note(at: number): boolean;
  /** The run of frames is over; the next `note` starts one rather than continuing this one. */
  forget(): void;
}

export function createObservationContinuity(
  tunables: GameplayTunables = DEFAULT_GAMEPLAY_TUNABLES,
): ObservationContinuity {
  const bound = tunables.pose.observationGapMs;
  let lastAt: number | null = null;

  return {
    note(at: number): boolean {
      const previous = lastAt;
      lastAt = at;
      if (previous === null) return false;
      const elapsed = at - previous;
      return elapsed < 0 || elapsed > bound;
    },

    forget(): void {
      lastAt = null;
    },
  };
}
